import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { getFileUrl, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import TeamMemberModal from "@/components/team/TeamMemberModal";
import { TEAM } from "@/constants/testIds";
import { ACADEMY_PERMISSIONS } from "@/lib/academyAccess";

const ROLE_LABELS = { admin: "Administración", academy: "Equipo" };
const ROLE_BADGE_STYLES = {
  admin: "bg-brand-light text-brand",
  academy: "bg-[#E4EEF2] text-[#3F6E80]",
};

export default function TeamPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { toast } = useToast();

  const loadMembers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team");
      setMembers(data);
    } catch (err) {
      toast({ title: "Error al cargar el equipo", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMembers(); }, []);

  const handleSubmit = async (form, existing) => {
    try {
      if (existing) {
        await api.put(`/team/${existing.id}`, form);
        // Fix F (2026-10-18): avisa si tambien se cambio la contraseña
        toast({ title: "Miembro actualizado", description: form.password ? `${form.name}: datos y contraseña actualizados.` : `${form.name} fue actualizado correctamente.` });
      } else {
        await api.post("/team", form);
        toast({ title: "Miembro agregado", description: `${form.name} ahora forma parte del equipo.` });
      }
      setModalOpen(false);
      setEditingMember(null);
      loadMembers();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/team/${deleteTarget.id}`);
      toast({ title: "Miembro eliminado", description: `${deleteTarget.name} fue eliminado del equipo.` });
      setDeleteTarget(null);
      loadMembers();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Equipo</h1>
          <p className="text-base text-muted-foreground mt-1">Las personas que entran al sistema y qué puede hacer cada una.</p>
        </div>
        <Button
          className="bg-brand hover:bg-brand-hover text-white"
          onClick={() => { setEditingMember(null); setModalOpen(true); }}
          data-testid={TEAM.addButton}
        >
          <Plus className="w-4 h-4" /> Agregar miembro
        </Button>
      </div>

      <p className="text-sm text-muted-foreground bg-[#E9F3F3] rounded-lg px-4 py-3" data-testid={TEAM.explainerBanner}>
        <strong className="text-foreground">Administración</strong> ve y hace todo. Al resto del equipo se le marcan los permisos: ejecutivo, finanzas, recepción, área académica o instructor.
      </p>

      <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-muted-foreground">Cargando equipo...</p>
        ) : members.length === 0 ? (
          <div className="p-12 text-center" data-testid={TEAM.emptyState}>
            <UserCog className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Aun no agregaste a nadie a tu equipo.</p>
          </div>
        ) : (
          <table className="w-full" data-testid={TEAM.table}>
            <thead>
              <tr className="text-xs uppercase tracking-[0.1em] text-muted-foreground bg-[#E9F3F3]">
                <th className="text-left font-medium py-3 px-4">Nombre</th>
                <th className="text-left font-medium py-3 px-4 hidden sm:table-cell">Email</th>
                <th className="text-left font-medium py-3 px-4">Rol</th>
                <th className="text-right font-medium py-3 px-4">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-b border-border hover:bg-background transition-colors" data-testid={`${TEAM.rowPrefix}${m.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-9 h-9">
                        <AvatarImage src={getFileUrl(m.photo_url)} alt={m.name} />
                        <AvatarFallback className="bg-brand-light text-brand text-xs">{m.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-foreground font-medium">{m.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-muted-foreground hidden sm:table-cell">{m.email}</td>
                  <td className="py-3 px-4">
                    <Badge className={`${ROLE_BADGE_STYLES[m.role]} border-none font-normal`}>{ROLE_LABELS[m.role]}</Badge>
                    {m.is_active === false && <Badge variant="outline" className="ml-1 font-normal">Desactivada</Badge>}
                    {m.role !== "admin" && (
                      <p className="text-xs text-muted-foreground mt-1" data-testid={`${TEAM.academyRolesPrefix}${m.id}`}>
                        {(m.academy_roles || []).length === 0 ? "Sin permisos" : ACADEMY_PERMISSIONS.filter((p) => m.academy_roles.includes(p.key)).map((p) => p.label).join(", ")}
                      </p>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => { setEditingMember(m); setModalOpen(true); }} data-testid={`${TEAM.editButtonPrefix}${m.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(m)} data-testid={`${TEAM.deleteButtonPrefix}${m.id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TeamMemberModal open={modalOpen} onOpenChange={setModalOpen} member={editingMember} onSubmit={handleSubmit} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar a {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer: pierde el acceso al sistema.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={TEAM.deleteCancelButton}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid={TEAM.deleteConfirmButton}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
